import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { categoriesAPI } from '../services/api';

interface Category {
  id: number;
  name: string;
  color: string;
  total_income?: number;
  total_expense?: number;
  net_amount?: number;
  monthly_amounts?: number[];
}

const COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#FFEAA7',
  '#DDA0DD',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE',
  '#82E0AA',
  '#F8C471',
  '#85C1E9',
];

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    color: COLORS[0],
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await categoriesAPI.getAll();
      console.log('Categories API Response:', response.data);
      
      // Handle the correct response structure: { success: true, data: [...] }
      const apiData = response.data;
      const categoriesData = apiData?.data || apiData; // Fallback to direct data if no wrapper
      
      // Ensure categories is always an array
      if (Array.isArray(categoriesData)) {
        setCategories(categoriesData);
        console.log('Categories loaded:', categoriesData.length, 'items');
      } else {
        console.warn('Categories data is not an array:', categoriesData);
        setCategories([]);
      }
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      setError(error.response?.data?.error || 'Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      color: COLORS[0],
    });
    setDialogOpen(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      color: category.color,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoriesAPI.delete(id);
        fetchCategories();
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to delete category');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, formData);
      } else {
        await categoriesAPI.create(formData);
      }

      setDialogOpen(false);
      fetchCategories();
    } catch (error: any) {
      setError(error.response?.data?.errors?.join(', ') || 'Failed to save category');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Categories</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Category
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {Array.isArray(categories) && categories.map((category) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: category.color,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h6" component="div">
                    {category.name}
                  </Typography>
                </Box>
                {category.total_expense !== undefined && (
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Total Expense: ₹{category.total_expense.toLocaleString()}
                    </Typography>
                    {category.total_income !== undefined && category.total_income > 0 && (
                      <Typography variant="body2" color="textSecondary">
                        Total Income: ₹{category.total_income.toLocaleString()}
                      </Typography>
                    )}
                    <Typography 
                      variant="body2" 
                      color={category.net_amount && category.net_amount >= 0 ? 'success.main' : 'error.main'}
                    >
                      Net: ₹{category.net_amount?.toLocaleString() || '0'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <IconButton size="small" onClick={() => handleEdit(category)}>
                  <Edit />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(category.id)}>
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add Category'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Color
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {COLORS.map((color) => (
              <Box
                key={color}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: color,
                  cursor: 'pointer',
                  border: formData.color === color ? '3px solid #000' : 'none',
                  '&:hover': {
                    border: '3px solid #666',
                  },
                }}
                onClick={() => setFormData({ ...formData, color })}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCategory ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesPage;
